import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const T = {
  ink:    '#111',
  muted:  '#888',
  border: '#e5e5e5',
  bg:     '#f7f7f7',
  font:   "'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif",
};

export default function VerifyEmail() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const email    = location.state?.email    || '';
  const password = location.state?.password || '';

  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [resent, setResent]   = useState(false);
  const inputs = useRef([]);

  useEffect(() => {
    inputs.current[0]?.focus();
  }, []);

  function handleChange(i, val) {
    const v = val.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    setError('');
    if (v && i < 5) inputs.current[i + 1]?.focus();
    if (next.every(d => d !== '')) submitCode(next.join(''));
  }

  function handleKeyDown(i, e) {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputs.current[i - 1]?.focus();
    }
  }

  function handlePaste(e) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length !== 6) return;
    const next = pasted.split('');
    setDigits(next);
    inputs.current[5]?.focus();
    submitCode(pasted);
  }

  async function submitCode(code) {
    if (code.length !== 6 || loading) return;
    setLoading(true);
    setError('');

    const res = await fetch('/api/otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'verify', email, code }),
    });
    const json = await res.json();

    if (!res.ok) {
      setError(json.error || 'Ungültiger Code.');
      setDigits(['', '', '', '', '', '']);
      inputs.current[0]?.focus();
      setLoading(false);
      return;
    }

    // Sign in now that account is verified
    await supabase.auth.signInWithPassword({ email, password });
    navigate('/pricing', { replace: true });
  }

  async function handleResend() {
    setResent(false);
    await fetch('/api/otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'resend', email }),
    });
    setResent(true);
    setDigits(['', '', '', '', '', '']);
    inputs.current[0]?.focus();
  }

  const code = digits.join('');

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, fontFamily: T.font }}>
      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Link to="/" style={{ textDecoration: 'none' }}>
            <span style={{ fontSize: 15, fontWeight: 900, letterSpacing: '2.5px', textTransform: 'uppercase', color: T.ink }}>
              IMMO<span style={{ color: T.ink }}>HQ</span>
            </span>
          </Link>
        </div>

        <div style={{ background: 'white', border: `1.5px solid ${T.border}`, borderRadius: 16, padding: '40px 36px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', textAlign: 'center' }}>

          {/* Icon */}
          <div style={{ width: 52, height: 52, background: '#f5f5f5', border: `1.5px solid ${T.border}`, borderRadius: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.ink} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </div>

          <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.5px', color: T.ink, margin: '0 0 10px' }}>
            Code eingeben
          </h1>
          <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.65, margin: '0 0 32px' }}>
            Wir haben einen 6-stelligen Code an<br />
            <strong style={{ color: T.ink }}>{email || 'deine E-Mail'}</strong> gesendet.
          </p>

          {/* 6-digit input */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 28 }} onPaste={handlePaste}>
            {digits.map((d, i) => (
              <input
                key={i}
                ref={el => inputs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                style={{
                  width: 48, height: 56,
                  textAlign: 'center',
                  fontSize: 24, fontWeight: 800,
                  letterSpacing: 0,
                  border: `1.5px solid ${error ? '#fca5a5' : d ? T.ink : T.border}`,
                  borderRadius: 10,
                  background: d ? '#f5f5f5' : 'white',
                  color: T.ink,
                  outline: 'none',
                  transition: 'border-color 0.15s, background 0.15s',
                  fontFamily: T.font,
                }}
                onFocus={e => { if (!error) e.target.style.borderColor = T.ink; }}
                onBlur={e => { if (!d && !error) e.target.style.borderColor = T.border; }}
              />
            ))}
          </div>

          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#b91c1c', marginBottom: 20 }}>
              {error}
            </div>
          )}

          {resent && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#166534', marginBottom: 20 }}>
              Neuer Code gesendet — bitte E-Mail prüfen.
            </div>
          )}

          <button
            onClick={() => submitCode(code)}
            disabled={code.length !== 6 || loading}
            style={{
              width: '100%', padding: '13px',
              background: code.length === 6 && !loading ? T.ink : '#d1d5db',
              color: 'white', border: 'none', borderRadius: 10,
              fontSize: 13, fontWeight: 700, cursor: code.length === 6 && !loading ? 'pointer' : 'not-allowed',
              fontFamily: T.font, transition: 'background 0.15s',
              boxShadow: code.length === 6 ? '0 4px 14px rgba(0,0,0,0.06)' : 'none',
            }}
          >
            {loading ? 'Überprüfe…' : 'Bestätigen →'}
          </button>

          <p style={{ fontSize: 12, color: T.muted, marginTop: 20, lineHeight: 1.6 }}>
            Keinen Code erhalten?{' '}
            <button onClick={handleResend} style={{ background: 'none', border: 'none', color: T.ink, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0, fontFamily: T.font }}>
              Erneut senden
            </button>
          </p>

        </div>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Link to="/register" style={{ fontSize: 12, color: T.muted, textDecoration: 'none' }}>
            ← Zurück zur Registrierung
          </Link>
        </div>

      </div>
    </div>
  );
}
